import React from 'react';
import { RecordedDocument, Transaction } from 'pages/Search/models';
import {
  Descriptions,
  Table,
  List,
  Col,
  Row,
  Typography,
  Button,
  Spin,
  Dropdown,
  Menu,
  Icon,
} from 'antd';
import {
  Date,
  Money,
  CommonTable,
  TableSizeNames,
  IconTypeNames,
  Sizes,
  PrintButton,
} from 'common';
import { RecordedDocumentReferences } from '..';
import { useDispatch, useSelector } from 'react-redux';
import { exportDetailsAsync } from 'pages/Search';
import { AppState } from 'store';
import ButtonGroup from 'antd/lib/button/button-group';
import { useAccount, useMsal } from '@azure/msal-react';
import { getAccessToken } from 'common/helpers';
import { InteractionStatus } from '@azure/msal-browser';

const { Text } = Typography;
const { Column, ColumnGroup } = Table;

type RecordedDocumentDetailsProps = {
  searchId: number;
  document: RecordedDocument;
};

const boroConvert = (boro: number): string => {
  switch (boro) {
    case 1:
      return 'New York';
    case 2:
      return 'Bronx';
    case 3:
      return 'Kings';
    case 4:
      return 'Queens';
    case 5:
      return 'Richmond';
    default:
      return 'ERROR';
  }
};

const RecordedDocumentDetails: React.FC<RecordedDocumentDetailsProps> = ({
  searchId,
  document,
}: RecordedDocumentDetailsProps) => {
  const dispatch = useDispatch();
  const { loading }: any = useSelector((state: AppState) => ({
    loading: state.searchExport.loading,
  }));
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const {
    boro,
    reelFileNumber,
    instr,
    crfn,
    dated,
    remarks,
    amount,
    documentType,
    dateRec,
    entered,
    lots,
    firstSideOfTransaction,
    secondSideOfTransaction,
    documentReferences,
    documentTypeDetails,
    documentID,
    pageOFileNumber,
  } = document;

  const {
    firstSideOfTransactionHeaderName,
    secondSideOfTransactionHeaderName,
  } = documentTypeDetails;

  const handleDetailsExport = React.useCallback(
    (format: string, action: 'print' | 'save') => {
      if (account && inProgress === InteractionStatus.None) {
        getAccessToken(instance, account, inProgress).then((response) => {
          if (response?.accessToken)
            dispatch(
              exportDetailsAsync(
                document,
                searchId,
                'recordedDocument',
                format,
                action,
                response.accessToken,
              ),
            );
        });
      }
    },
    [dispatch, instance, account, inProgress, document, searchId],
  );

  return (
    <Spin spinning={loading} size={Sizes.LARGE} tip={'Printing...'}>
      <Descriptions
        size="small"
        title={
          <Row type="flex" align="middle" justify="space-between">
            <Text>Instrument</Text>
            <ButtonGroup>
              <PrintButton
                size={TableSizeNames.SMALL}
                icon={IconTypeNames.PRINTER}
                onClick={() => handleDetailsExport('html', 'print')}
              />
              <Dropdown
                overlay={
                  <Menu onClick={({ key }) => handleDetailsExport(key, 'save')}>
                    <Menu.Item key="doc">
                      <Icon type="file-word" />
                      MS Word
                    </Menu.Item>
                    <Menu.Item key="pdf">
                      <Icon type="file-pdf" />
                      PDF
                    </Menu.Item>
                    <Menu.Item key="html">
                      <Icon type="html5" />
                      HTML
                    </Menu.Item>
                    <Menu.Item key="txt">
                      <Icon type="file-text" />
                      Text
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button
                  type="primary"
                  size={TableSizeNames.SMALL}
                  shape="round"
                  icon="download"
                >
                  Export&nbsp;
                  <Icon type="down" />
                </Button>
              </Dropdown>
            </ButtonGroup>
          </Row>
        }
        column={3}
      >
        <Descriptions.Item label="Document ID">{documentID}</Descriptions.Item>
        <Descriptions.Item label="CRFN">{crfn}</Descriptions.Item>
        <Descriptions.Item label="Instrument">{instr}</Descriptions.Item>

        {documentType === 'uniformCommercialCodes' ||
        documentType === 'federalTaxLiens' ? (
          <Descriptions.Item label="Amount">
            <Money>{amount}</Money>
          </Descriptions.Item>
        ) : (
          amount > 0 && (
            <Descriptions.Item label="Amount">
              <Money>{amount}</Money>
            </Descriptions.Item>
          )
        )}

        <Descriptions.Item label="Recorded">
          <Date>{dateRec}</Date>
        </Descriptions.Item>

        {documentType === 'recordedDocuments' && (
          <Descriptions.Item label="Dated">
            <Date>{dated}</Date>
          </Descriptions.Item>
        )}

        {documentType === 'recordedDocuments' && (
          <Descriptions.Item label="Entered">
            <Date>{entered}</Date>
          </Descriptions.Item>
        )}

        {documentType === 'recordedDocuments' && (
          <>
            <Descriptions.Item label="Reel">
              {crfn === '' ? reelFileNumber : ''}
            </Descriptions.Item>
            <Descriptions.Item label="Page">
              {crfn === '' ? pageOFileNumber : ''}
            </Descriptions.Item>
          </>
        )}
        {(documentType === 'uniformCommercialCodes' ||
          documentType === 'federalTaxLiens') && (
          <>
            <Descriptions.Item label="File Num">
              {reelFileNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Orig Doc">
              {pageOFileNumber}
            </Descriptions.Item>
          </>
        )}
        <Descriptions.Item label="County">
          {boroConvert(boro)}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions size="small" title="Parcel Information">
        {lots && lots.length > 0 && (
          <Descriptions.Item>
            <Text>There are {lots.length} lots in the table</Text>
            <CommonTable items={lots}>
              <Column title="Block" dataIndex="block" />
              <Column title="Lot Number" dataIndex="lotNumber" />
              <Column title="Ease" dataIndex="ease" />
              <Column title="Part" dataIndex="lot_Part" />
              <Column title="Type" dataIndex="lot_Type" />
              <Column title="Air Rights" dataIndex="air_Rights" />
              <Column title="Sub Rights" dataIndex="sub_Rights" />
              <ColumnGroup title="Address">
                <Column title="Number" dataIndex="streetNumber" />
                <Column title="Street" dataIndex="streetName" />
              </ColumnGroup>
              <Column title="Unit" dataIndex="unitNumber" />
            </CommonTable>
          </Descriptions.Item>
        )}
      </Descriptions>

      {((firstSideOfTransaction && firstSideOfTransaction.length > 0) ||
        (secondSideOfTransaction && secondSideOfTransaction.length > 0)) && (
        <Row type="flex" align="top" gutter={30}>
          <Col>
            {firstSideOfTransaction && firstSideOfTransaction.length > 0 && (
              <List
                header={
                  <Text strong>
                    {firstSideOfTransactionHeaderName
                      ? firstSideOfTransactionHeaderName
                      : 'Party 1 Names'}
                  </Text>
                }
                size="small"
                dataSource={firstSideOfTransaction}
                renderItem={(transaction: Transaction) => (
                  <>
                    <List.Item>{transaction.name}</List.Item>
                    <List.Item>{`${transaction.address}`}</List.Item>
                  </>
                )}
              />
            )}
          </Col>
          <Col>
            {secondSideOfTransaction && secondSideOfTransaction.length > 0 && (
              <List
                header={
                  <Text strong>
                    {secondSideOfTransactionHeaderName
                      ? secondSideOfTransactionHeaderName
                      : 'Party 2 Names'}
                  </Text>
                }
                size="small"
                dataSource={secondSideOfTransaction}
                renderItem={(transaction: Transaction) => (
                  <>
                    <List.Item>{transaction.name}</List.Item>
                    <List.Item>{`${transaction.address}`}</List.Item>
                  </>
                )}
              />
            )}
          </Col>
        </Row>
      )}

      {remarks && remarks.length > 0 && (
        <List
          header={<Text strong>Remarks</Text>}
          size="small"
          dataSource={remarks}
          renderItem={(item) => <List.Item>{item.remarkText}</List.Item>}
        />
      )}

      {documentReferences && documentReferences.length > 0 && (
        <Descriptions size="small" title="Document References">
          <Descriptions.Item>
            <RecordedDocumentReferences items={documentReferences} />
          </Descriptions.Item>
        </Descriptions>
      )}
    </Spin>
  );
};

export default RecordedDocumentDetails;
